/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import Helmet from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

const LOCAL_BUSINESS_SCHEMA = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "Architect"],
  "@id": "https://www.davidlwaldronarchitect.com/#business",
  "name": "David L. Waldron Architect",
  "description": "Licensed architect in Friday Harbor on San Juan Island, Washington, with over 45 years of experience in residential, commercial, and historic preservation architecture.",
  "url": "https://www.davidlwaldronarchitect.com/",
  "telephone": "+1-360-378-3070",
  "faxNumber": "+1-360-378-9527",
  "email": "davidwaldron@interisland.net",
  "foundingDate": "1980",
  "founder": {
    "@type": "Person",
    "name": "David L. Waldron",
    "jobTitle": "Architect",
    "alumniOf": [
      {
        "@type": "CollegeOrUniversity",
        "name": "University of Washington",
        "department": "School of Architecture"
      },
      {
        "@type": "CollegeOrUniversity",
        "name": "Willamette University"
      }
    ]
  },
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "310 Spring Street",
    "postOfficeBoxNumber": "P.O. Box 2505",
    "addressLocality": "Friday Harbor",
    "addressRegion": "WA",
    "postalCode": "98250",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": 48.5338148,
    "longitude": -123.0179193
  },
  "areaServed": [
    {
      "@type": "Place",
      "name": "San Juan Island, Washington"
    },
    {
      "@type": "Place",
      "name": "San Juan Islands, Washington"
    }
  ],
  "knowsAbout": [
    "Residential Architecture",
    "Commercial Architecture",
    "Historic Preservation",
    "Passive Solar Design",
    "Building Restoration",
    "Affordable Housing Design"
  ],
  "sameAs": [
    "https://sanjuanislands.com/directory/listings/david-l-waldron-architect/",
    "https://www.thebluebook.com/iProView/960159/david-l-waldron/architects/locations-contacts/"
  ]
}

function SEO({ description, lang, meta, keywords, title, pathname }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  )

  const metaDescription = description || site.siteMetadata.description
  const siteUrl = site.siteMetadata.siteUrl
  const canonical = pathname ? `${siteUrl}${pathname}` : siteUrl

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={title}
      titleTemplate={`%s | ${site.siteMetadata.title}`}
      link={[
        {
          rel: "canonical",
          href: canonical,
        },
      ]}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        {
          property: `og:title`,
          content: title,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:url`,
          content: canonical,
        },
        {
          property: `og:site_name`,
          content: site.siteMetadata.title,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:title`,
          content: title,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `geo.region`,
          content: `US-WA`,
        },
        {
          name: `geo.placename`,
          content: `Friday Harbor`,
        },
        {
          name: `geo.position`,
          content: `48.5338148;-123.0179193`,
        },
      ]
        .concat(
          keywords.length > 0
            ? {
                name: `keywords`,
                content: keywords.join(`, `),
              }
            : []
        )
        .concat(meta)}
    >
      <script type="application/ld+json">
        {JSON.stringify(LOCAL_BUSINESS_SCHEMA)}
      </script>
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
  description: ``,
  pathname: ``,
}

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
  pathname: PropTypes.string,
}

export default SEO
